/**
 * RGBA color handling as plain data. No DOM dependency: the visual layer
 * converts {r,g,b,a} to CSS strings / ImageData itself.
 */

import { HEX_LENGTH } from "./limits.js";
import { PixelwerkstattError } from "./errors.js";

/** A single RGBA color, each channel 0–255. */
export interface Rgba {
  r: number;
  g: number;
  b: number;
  /** 0 = fully transparent, 255 = opaque. */
  a: number;
}

const CHANNEL_MIN = 0;
const CHANNEL_MAX = 255;

export function isRgba(value: unknown): value is Rgba {
  if (typeof value !== "object" || value === null) return false;
  const v = value as Record<string, unknown>;
  const channels = [v.r, v.g, v.b, v.a];
  return channels.every(
    (c) => typeof c === "number" && Number.isInteger(c) && c >= CHANNEL_MIN && c <= CHANNEL_MAX,
  );
}

export function rgba(r: number, g: number, b: number, a: number = 255): Rgba {
  for (const c of [r, g, b, a]) {
    if (!Number.isInteger(c) || c < CHANNEL_MIN || c > CHANNEL_MAX) {
      throw new PixelwerkstattError(
        "COLOR_COMPONENT_RANGE",
        `Farbkanal muss eine ganze Zahl zwischen 0 und 255 sein (erhalten: ${c}).`,
      );
    }
  }
  return { r, g, b, a };
}

/** Validate a "#RRGGBB" string; returns the uppercase canonical form. */
export function isValidHexColor(value: string): value is string {
  if (value.length !== HEX_LENGTH || value[0] !== "#") return false;
  return /^[0-9a-fA-F]{6}$/.test(value.slice(1));
}

export function parseHexColor(value: string): Rgba {
  if (!isValidHexColor(value)) {
    throw new PixelwerkstattError(
      "COLOR_FORMAT",
      `Farbe bitte als #RRGGBB eingeben (erhalten: "${value}").`,
    );
  }
  const n = Number.parseInt(value.slice(1), 16);
  return {
    r: (n >> 16) & 0xff,
    g: (n >> 8) & 0xff,
    b: n & 0xff,
    a: 255,
  };
}

const HEX_DIGITS = "0123456789abcdef";

function toHexByte(v: number): string {
  return HEX_DIGITS[(v >> 4) & 0xf]! + HEX_DIGITS[v & 0xf]!;
}

/** Format as uppercase "#RRGGBB" (alpha is dropped, matching legacy export). */
export function toHexColor(color: Rgba): string {
  return `#${toHexByte(color.r)}${toHexByte(color.g)}${toHexByte(color.b)}`.toUpperCase();
}

export function rgbaEqual(a: Rgba, b: Rgba): boolean {
  return a.r === b.r && a.g === b.g && a.b === b.b && a.a === b.a;
}

export function cloneRgba(color: Rgba): Rgba {
  return { r: color.r, g: color.g, b: color.b, a: color.a };
}
